import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApolloQueryResult } from 'apollo-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MasterDataEnum } from '../enum/master-data.enum';
import { NewRequestStatusEnum } from '../enum/request-status.enum';
import { GraphqlQueryObject, GraphqlQuerySortOptions, OrderByEnum } from '../gql/common.gql';
import { SegmentList } from '../gql/segmentList.gql';
import { StoreListSearchCriteria } from '../models';
import { MetricAction, MetricActionType, MetricModule, MetricType, Metrics } from '../models/metrics';
import {
  OrderFixAssetCreateRequest,
  OrderFixAssetRequestViewResponse
} from '../models/order-request-fix-asset.model';
import {
  ApproveOrderRequest,
  OrderId,
  OrderRequestListResponse,
  OrderRequestListSearchCriteria,
  OrderRequestValidate,
  OrderRequestViewResponse,
  ProductOrderItem
} from '../models/order-request.model';
import { BaseService } from './base.service';
import { MasterService } from './master.service';

@Injectable()
export class OrderFixAssetRequestService extends BaseService {
  public headers: HttpHeaders;

  constructor(
    private readonly http: HttpClient,
    private readonly masterService: MasterService
  ) {
    super();
    this.envService = this.env.services.orderRequestFixAsset;
    this.headers = new HttpHeaders(this.envService.headers);
  }

  public searchByCriteria(criteria: OrderRequestListSearchCriteria): Observable<OrderRequestListResponse> {
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.ORDER,
          route: this.envService.url,
          action: MetricActionType.SEARCH
        }
      ]
    };
    const params = this.getParams(criteria);

    return this.http.get<OrderRequestListResponse>(this.getUrl(), {
      headers: this.loaderHeaders(null, metricsReq),
      observe: 'body',
      params
    });
  }

  public getOrderById(orderId: OrderId): Observable<OrderFixAssetRequestViewResponse> {
    const url = this.getFullUrl(this.envService.view, {
      orderId: orderId.orderId
    });

    return this.http.get<any>(url, {
      headers: this.headers,
      observe: 'body'
    });
  }

  public saveRequest(OrderRequest: OrderFixAssetCreateRequest): Observable<OrderFixAssetRequestViewResponse> {
    const url = this.getFullUrl(this.envService.save);

    return this.http.put<any>(url, OrderRequest, { headers: this.loaderHeaders(2000) });
  }

  public submitRequest(OrderRequest: OrderFixAssetCreateRequest): Observable<OrderFixAssetRequestViewResponse> {
    const url =
      OrderRequest.status === NewRequestStatusEnum.ASSIGN_STORE_EQUIPMENT
        ? this.env.serverUrl +
          this.env.services.orderRequest.url +
          this.env.services.orderRequestFixAsset.storeUseSubmit
        : this.getFullUrl(this.envService.submit);

    return this.http.post<any>(url, OrderRequest, {
      headers: this.loaderHeaders(2000, this.prepareOrderMatrix(OrderRequest))
    });
  }

  public approveRequest(request: ApproveOrderRequest): Observable<OrderRequestViewResponse> {
    const url = this.getFullUrl(this.envService.approve);

    return this.http.put<OrderRequestViewResponse>(url, request, {
      headers: this.loaderHeaders(2000),
      observe: 'body'
    });
  }

  public deleteByRequestId(orderId: OrderId): Observable<HttpResponse<any>> {
    const url = this.getFullUrl(this.envService.delete, {
      orderId: orderId.orderId
    });

    return this.http.delete<any>(url, { headers: this.loaderHeaders(2000), observe: 'body' });
  }

  public getHistoryLogs(orderId: OrderId): Observable<any> {
    const url = this.getFullUrl(this.envService.history, {
      orderId: orderId.orderId
    });
    return this.http.get<any>(url, { headers: this.headers, observe: 'body' });
  }

  public validateOrderList(
    storeNo: string,
    orderType: string,
    itemList: Array<OrderRequestValidate>
  ): Observable<ProductOrderItem[]> {
    const params = this.getParams({ storeNo, orderType });
    const url = this.getFullUrl(this.envService.validateOrderList);

    return this.http.post<ProductOrderItem[]>(url, itemList, {
      headers: this.headers,
      observe: 'body',
      params
    });
  }

  public searchStoreByCriteria(criteria: StoreListSearchCriteria): Observable<any> {
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.ORDER_FIX_ASSET,
          route: this.envService.getStore,
          action: MetricActionType.SEARCH
        }
      ]
    };
    const url = this.getFullUrl(this.envService.getStore);
    const params = this.getParams(criteria);
    return this.http
      .get<any>(url, {
        headers: this.loaderHeaders(null, metricsReq),
        observe: 'body',
        params
      })
      .pipe(
        map(res => {
          if (res && res.content) {
            res.content.forEach(item => {
              item.storeCodeName = `${item.code}-${item.name}`;
            });
          }
          return res;
        })
      );
  }

  public getSegmentListData(): Observable<ApolloQueryResult<SegmentList>> {
    const segmentListQuery = new GraphqlQueryObject();
    segmentListQuery.name = MasterDataEnum.SEGMENT;
    segmentListQuery.sort = { orderBy: OrderByEnum.CODE } as GraphqlQuerySortOptions;

    return this.masterService.getMasterDataByNames([segmentListQuery]);
  }

  public downloadFileTemplate(): Observable<any> {
    const url = this.getFullUrl(this.envService.downloadFileTemplate);
    return this.http.get<any>(url, { headers: this.headers, responseType: 'blob' as 'json' });
  }

  public importFileRequest(file: File, guid: string): Observable<any> {
    const url = this.getFullUrl(this.envService.importValidate);
    let formData = null;
    formData = new FormData();
    formData.append('guid', guid);
    formData.append('file', file, file.name);

    this.headers.append('Content-Type', 'multipart/form-data');

    return this.http.post<any>(url, formData, {
      headers: this.loaderHeaders(),
      observe: 'body'
    });
  }

  public submitFileRequest(guid: string): Observable<any> {
    const url = this.getFullUrl(this.envService.importSubmit);
    const params = this.getParams({ guid });
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.ORDER_REQUEST,
          path: `${this.envService.importSubmit}`
        },
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.ORDER_REQUEST,
          route: `${this.envService.importSubmit}`,
          action: MetricAction.IMPORT
        }
      ]
    };
    return this.http.post<any>(url, null, { headers: this.loaderHeaders(0, metricsReq), observe: 'body', params });
  }

  prepareOrderMatrix(request: OrderFixAssetCreateRequest): Metrics {
    let module: string = null;
    let path: string = null;

    if (request.status === NewRequestStatusEnum.DRAFT) {
      module = MetricModule.ORDER_REQUEST;
      path = this.envService.submit;
    } else if (request.status === NewRequestStatusEnum.ASSIGN_STORE_EQUIPMENT) {
      module = MetricModule.ORDER;
      path = [this.env.services.orderRequest.url, this.env.services.orderRequestFixAsset.storeUseSubmit].join('');
    }

    if (module && path) {
      return {
        metrics: [
          {
            metricType: MetricType.COUNTER,
            module: module,
            path: path
          },
          {
            metricType: MetricType.HISTOGRAM,
            module: module,
            action: MetricAction.CREATE,
            type: request.orderSubType,
            items: 1
          }
        ]
      };
    }

    return null;
  }
}
